import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PlaceIcon from '@mui/icons-material/Place'
import PhoneIcon from '@mui/icons-material/Phone'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'

import Navbar from '../components/navbar'
import NavbarFooter from '../components/navbar/NavbarFooter'
import CasinoRating from '../components/casino/CasinoRating'
import CasinoMinBets from '../components/casino/CasinoMinBets'
import ReportMinBetsDialog from '../components/casino/ReportMinBetsDialog'
import RefreshIndicator from '../components/RefreshIndicator'
import ResponsibleGamingFooter from '../components/ResponsibleGamingFooter'
import usePollingRefresh from '../hooks/usePollingRefresh'
import ICasino, { IMinBet } from '../models/ICasino'
import { useAppState } from '../reduxish/appState'
import { addFavoriteCasino, removeFavoriteCasino } from '../api/favoritesApi'
import { apiFetch } from '../api/http'

export default function CasinoDetailPage() {
    const { slug } = useParams<{ slug: string }>()
    const { state, dispatch } = useAppState()
    const isAuthenticated = state.auth?.isAuthenticated ?? false

    const handleUnauthFavorite = () => {
        dispatch({ type: 'OK_DIALOG_OPEN_FULL_UPDATE', open: true, title: 'Sign in to save favorites', text: 'Sign in to save your favorite casinos and jump straight to them from the home screen!' })
    }
    const handleUnauthReport = () => {
        dispatch({ type: 'OK_DIALOG_OPEN_FULL_UPDATE', open: true, title: 'Sign in to contribute', text: 'Sign in to report table minimums. Your contributions help every player find the best deal!' })
    }

    const isOnline = state.pwa.isOnline
    const [casino, setCasino] = useState<ICasino | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [errorMsg, setErrorMsg] = useState('')
    const [reportOpen, setReportOpen] = useState(false)
    const [favoriteBusy, setFavoriteBusy] = useState(false)

    const casinoId = slug?.split('-')[0] ?? ''

    const fetchCasino = useCallback(async (signal?: AbortSignal) => {
        if (!casinoId) return
        const res = await apiFetch(`/api/v1/casinos/${casinoId}`, { signal })
        if (!res.ok) throw new Error(`Failed to fetch casino: ${res.status}`)
        const data: ICasino = await res.json()
        setCasino(prev => {
            // Preserve existing reference if min-bets are unchanged so SlotNumber
            // doesn't reel for unchanged values.
            if (prev && prev.id === data.id) {
                return { ...prev, ...data }
            }
            return data
        })
    }, [casinoId])

    useEffect(() => {
        if (!casinoId) return
        setIsLoading(true)
        setErrorMsg('')
        fetchCasino()
            .then(() => setIsLoading(false))
            .catch(() => {
                setErrorMsg('There was an error retrieving the casino data. Check your internet connection and try refreshing.')
                setIsLoading(false)
            })
    }, [casinoId, fetchCasino])

    const { isRefreshing, lastUpdated, refreshNow } = usePollingRefresh(
        (signal) => fetchCasino(signal),
        !!casinoId && !isLoading && !errorMsg,
    )

    const handleAcceptedBet = (result: { gameType: string, currentMinBet?: number }) => {
        if (result.currentMinBet == null) return
        setCasino(prev => {
            if (!prev) return prev
            const updatedBet: IMinBet = {
                gameType: result.gameType,
                minBet: result.currentMinBet!,
                verified: true,
                reportedAt: new Date().toISOString(),
            }
            const existing = prev.minBets ?? []
            const idx = existing.findIndex(b => b.gameType === result.gameType)
            const next = idx >= 0
                ? existing.map((b, i) => (i === idx ? updatedBet : b))
                : [...existing, updatedBet]
            return { ...prev, minBets: next }
        })
    }

    const lastReportedAt = useMemo(() => {
        const times = (casino?.minBets ?? [])
            .map(b => b.reportedAt)
            .filter((t): t is string => !!t)
            .map(t => new Date(t).getTime())
            .filter(t => !Number.isNaN(t))
        return times.length ? new Date(Math.max(...times)) : null
    }, [casino])

    const isFavorite = casino
        ? (state.favoriteOverrides[casino.id] ?? casino.isFavorite ?? false)
        : false

    const handleToggleFavorite = async () => {
        if (!casino || favoriteBusy) return
        const next = !isFavorite
        setFavoriteBusy(true)
        // Optimistic update — flip both local casino state and the redux override
        // so the home-page list filter sees the change immediately.
        setCasino(prev => prev ? { ...prev, isFavorite: next } : prev)
        dispatch({ type: 'FAVORITE_TOGGLE', casinoId: casino.id, isFavorite: next })
        try {
            if (next) {
                await addFavoriteCasino(casino.id)
            } else {
                await removeFavoriteCasino(casino.id)
            }
        } catch (e) {
            // Revert on failure
            setCasino(prev => prev ? { ...prev, isFavorite: !next } : prev)
            dispatch({ type: 'FAVORITE_TOGGLE', casinoId: casino.id, isFavorite: !next })
            const msg = e instanceof Error ? e.message : 'Failed to update favorite'
            dispatch({ type: 'OK_DIALOG_OPEN_FULL_UPDATE', open: true, title: 'Favorite', text: msg })
        } finally {
            setFavoriteBusy(false)
        }
    }

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Navbar />
            <Container
                component="main"
                sx={{ flex: 1, display: 'flex', flexDirection: 'column', mt: 4, pb: { xs: 3, sm: 0 } }}
            >
                {isLoading
                    ? <CircularProgress />
                    : errorMsg
                        ? <Typography color="error">{errorMsg}</Typography>
                        : casino && (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                    <Typography variant="h4" sx={{ flex: 1, minWidth: 0, m: 0, wordBreak: 'break-word' }}>
                                        {casino.website ? (
                                            <Link
                                                href={casino.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                underline="hover"
                                                color="inherit"
                                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                {casino.name}
                                                <OpenInNewIcon fontSize="small" />
                                            </Link>
                                        ) : casino.name}
                                    </Typography>
                                    <Tooltip title={
                                        !isAuthenticated
                                            ? 'Sign in to save your favorite casinos'
                                            : !isOnline
                                                ? 'You are offline'
                                                : (isFavorite ? 'Remove from favorites' : 'Add to favorites')
                                    }>
                                        <span style={{ flexShrink: 0 }}>
                                            <IconButton
                                                aria-label={isFavorite ? 'remove from favorites' : 'add to favorites'}
                                                onClick={!isAuthenticated ? handleUnauthFavorite : handleToggleFavorite}
                                                disabled={isAuthenticated && (!isOnline || favoriteBusy)}
                                                sx={!isAuthenticated ? { opacity: 0.38 } : undefined}
                                                size="large"
                                            >
                                                {isFavorite
                                                    ? <FavoriteIcon sx={{ color: 'error.main' }} />
                                                    : <FavoriteBorderIcon />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                <CasinoRating rating={casino.rating} userRatings={casino.userRatings} forceNumber />
                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body1">
                                        <Link
                                            href={
                                                casino.latitude != null && casino.longitude != null
                                                    ? `https://www.google.com/maps/search/?api=1&query=${casino.latitude},${casino.longitude}${casino.googlePlacesId ? `&query_place_id=${encodeURIComponent(casino.googlePlacesId)}` : ''}`
                                                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(casino.address)}`
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            color="inherit"
                                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            <PlaceIcon fontSize="small" />
                                            {casino.address}
                                        </Link>
                                    </Typography>
                                    {casino.phoneNumber && (
                                        <Typography variant="body1">
                                            <Link
                                                href={`tel:${casino.phoneNumber.replace(/[^+\d]/g, '')}`}
                                                underline="hover"
                                                color="inherit"
                                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                <PhoneIcon fontSize="small" />
                                                {casino.phoneNumber}
                                            </Link>
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ mt: { xs: 2, sm: 5 }, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>Minimum bets</Typography>
                                    <RefreshIndicator
                                        isRefreshing={isRefreshing}
                                        lastUpdated={lastUpdated}
                                        onRefresh={refreshNow}
                                    />
                                </Box>
                                <Box sx={{ mt: 1 }}>
                                    <CasinoMinBets casino={casino} />
                                </Box>
                                {lastReportedAt && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                                        Last reported {lastReportedAt.toLocaleString()}
                                    </Typography>
                                )}
                                {casino.additionalInfo && (
                                    <Typography variant="body1" sx={{ mt: 2 }}>{casino.additionalInfo}</Typography>
                                )}
                                <Box
                                    sx={{
                                        mt: 'auto',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: { xs: 'center', md: 'flex-end' },
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        disabled={isAuthenticated && !isOnline}
                                        onClick={!isAuthenticated ? handleUnauthReport : () => setReportOpen(true)}
                                        sx={!isAuthenticated ? { opacity: 0.38 } : undefined}
                                    >
                                        Report prices
                                    </Button>
                                    {isAuthenticated && !isOnline && (
                                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                            Reconnect to report prices.
                                        </Typography>
                                    )}
                                </Box>
                                <ReportMinBetsDialog
                                    open={reportOpen}
                                    onClose={() => setReportOpen(false)}
                                    casino={casino}
                                    onAcceptedBet={handleAcceptedBet}
                                />
                            </>
                        )
                }
            </Container>
            <ResponsibleGamingFooter />
            <NavbarFooter />
        </Box>
    )
}
